import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, LogOut, Building2, Users, FileText, TrendingUp, Film, Globe2, Loader2, Activity, Mail, Shield, Server, CreditCard, Search } from 'lucide-react';
import { adminAuth, adminApi } from '../utils/api';

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-navy-600" />
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-bold text-navy-900">{value ?? '--'}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function slugify(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const TABS = [
  { id: 'overview',       label: 'Overview',       icon: TrendingUp },
  { id: 'organizations',  label: 'Organizations',  icon: Building2 },
  { id: 'trials',         label: 'Trials & Billing', icon: CreditCard },
  { id: 'usage',          label: 'Usage',          icon: Activity },
  { id: 'countries',      label: 'Countries',      icon: Globe2 },
  { id: 'contacts',       label: 'Contacts',       icon: Mail },
  { id: 'content',        label: 'Content',        icon: FileText },
  { id: 'security',       label: 'Security',       icon: Shield },
  { id: 'system_health',  label: 'System Health',  icon: Server },
];

// ── CONTENT TAB (article management) ─────────────────────────────────────
function ContentTab({ data }) {
  const [articles, setArticles] = useState(data || []);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    title: '', heading1: '', body1: '', heading2: '', body2: '', heading3: '', body3: '',
    author: 'Tactmark Editorial', category: 'Analysis', status: 'published',
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body1) { setError('Title and at least one section are required.'); return; }
    setSaving(true); setError('');
    try {
      const sections = [{ heading: form.heading1 || 'Overview', paragraphs: form.body1.split('\n\n').filter(Boolean) }];
      if (form.body2) sections.push({ heading: form.heading2 || 'More detail', paragraphs: form.body2.split('\n\n').filter(Boolean) });
      if (form.body3) sections.push({ heading: form.heading3 || 'Coaching view', paragraphs: form.body3.split('\n\n').filter(Boolean) });
      const slug = slugify(form.title);
      const res = await fetch(`/api/platform-admin?section=content_write`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('tactmark_admin_token')}` },
        body: JSON.stringify({ slug, title: form.title, author: form.author, category: form.category, status: form.status, sections }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Save failed');
      setShowForm(false);
      setArticles(prev => [json.article, ...prev]);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const filtered = articles.filter(a => !search || a.title?.toLowerCase().includes(search.toLowerCase()) || a.slug?.includes(search.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-navy-900 font-semibold text-lg">Content ({articles.length} articles)</h2>
        <div className="flex gap-2">
          <div className="relative"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-48" /></div>
          <button onClick={() => setShowForm(!showForm)} className="bg-navy-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy-800">{showForm ? 'Cancel' : '+ New article'}</button>
        </div>
      </div>
      {showForm && (
        <form onSubmit={handleSave} className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6 space-y-4">
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <input required value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Article title" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <div className="grid grid-cols-3 gap-3">
            <input value={form.author} onChange={e => setForm(f => ({...f, author: e.target.value}))} placeholder="Author" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} placeholder="Category" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="published">Published</option><option value="draft">Draft</option>
            </select>
          </div>
          {[1,2,3].map(i => (
            <div key={i} className="space-y-2">
              <input value={form[`heading${i}`]} onChange={e => setForm(f => ({...f, [`heading${i}`]: e.target.value}))} placeholder={`Section ${i} heading`} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              <textarea value={form[`body${i}`]} onChange={e => setForm(f => ({...f, [`body${i}`]: e.target.value}))} rows={4} placeholder={`Section ${i} body (double newline = new paragraph)`} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          ))}
          <button type="submit" disabled={saving} className="bg-navy-900 text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50">{saving ? 'Saving...' : 'Publish article'}</button>
        </form>
      )}
      <div className="border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Published</th><th className="text-left px-4 py-3">Slug</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.slice(0,100).map(a => (
              <tr key={a.id}><td className="px-4 py-3 text-navy-900 font-medium max-w-xs truncate">{a.title}</td><td className="px-4 py-3 text-gray-600">{a.category}</td><td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${a.status==='published'?'bg-green-50 text-green-700':'bg-gray-100 text-gray-500'}`}>{a.status}</span></td><td className="px-4 py-3 text-gray-500">{a.publish_date || a.created_at?.slice(0,10)}</td><td className="px-4 py-3 text-gray-400 text-xs font-mono">{a.slug}</td></tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-center py-8 text-gray-400">No articles found.</p>}
      </div>
    </div>
  );
}

export default function PlatformAdmin() {
  const [data, setData] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [orgSearch, setOrgSearch] = useState('');
  const [orgActionError, setOrgActionError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    adminAuth.me()
      .then(({ admin }) => {
        setAdminEmail(admin.email);
        return Promise.all([
          adminApi.getOverview(),
          adminApi.getCountries(),
          adminApi.getOrgs(),
          adminApi.getReportQuality(),
          adminApi.getRecentSignups(),
          fetch('/api/platform-admin?section=usage',    { headers: { Authorization: `Bearer ${localStorage.getItem('tactmark_admin_token')}` } }).then(r=>r.json()).catch(()=>({})),
          fetch('/api/platform-admin?section=contacts', { headers: { Authorization: `Bearer ${localStorage.getItem('tactmark_admin_token')}` } }).then(r=>r.json()).catch(()=>({})),
          fetch('/api/platform-admin?section=content',  { headers: { Authorization: `Bearer ${localStorage.getItem('tactmark_admin_token')}` } }).then(r=>r.json()).catch(()=>({})),
          fetch('/api/platform-admin?section=security', { headers: { Authorization: `Bearer ${localStorage.getItem('tactmark_admin_token')}` } }).then(r=>r.json()).catch(()=>({})),
          fetch('/api/platform-admin?section=system_health', { headers: { Authorization: `Bearer ${localStorage.getItem('tactmark_admin_token')}` } }).then(r=>r.json()).catch(()=>({})),
          fetch('/api/platform-admin?section=trials',   { headers: { Authorization: `Bearer ${localStorage.getItem('tactmark_admin_token')}` } }).then(r=>r.json()).catch(()=>({})),
        ]);
      })
      .then(([ovRes, ctRes, orgsRes, rqRes, sgRes, usageRes, contactsRes, contentRes, secRes, sysRes, trialsRes]) => {
        setData({
          overview:      ovRes.overview || ovRes,
          countries:     ctRes.countries || [],
          organizations: orgsRes.organizations || [],
          reportQuality: rqRes.reportQuality || rqRes.report_quality || {},
          recentSignups: sgRes.recentSignups || [],
          usage:         usageRes,
          contacts:      contactsRes.contacts || [],
          content:       contentRes.articles || [],
          security:      secRes,
          systemHealth:  sysRes.checks || [],
          trials:        trialsRes,
        });
      })
      .catch((e) => {
        if (e.status === 401) { localStorage.removeItem('tactmark_admin_token'); navigate('/admin/login'); return; }
        setError(e.message || 'Could not load admin data');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, []);

  const handleLogout = async () => {
    await adminAuth.logout();
    localStorage.removeItem('tactmark_admin_token');
    navigate('/admin/login');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (error)   return <div className="max-w-xl mx-auto py-24 text-center text-red-600">{error}</div>;
  if (!data)   return null;

  const { overview={}, countries=[], organizations=[], usage={}, contacts=[], content=[], security={}, systemHealth=[], trials={} } = data;
  const filteredOrgs = orgSearch ? organizations.filter(o => o.name?.toLowerCase().includes(orgSearch.toLowerCase()) || o.country?.toLowerCase().includes(orgSearch.toLowerCase())) : organizations;

  const federationEnabled = (o) => {
    const v = o.product_federation_enabled ?? o.productFederationEnabled;
    return Number(v) === 1;
  };
  const postMatchEnabled = (o) => {
    const v = o.product_post_match_enabled ?? o.productPostMatchEnabled;
    if (v === undefined || v === null) return true;
    return Number(v) === 1;
  };

  const toggleFederation = async (org) => {
    const next = federationEnabled(org) ? 0 : 1;
    setOrgActionError('');
    try {
      await adminApi.updateOrgProducts({ organizationId: org.id, productFederationEnabled: next });
      setData((prev) => ({
        ...prev,
        organizations: (prev.organizations || []).map((row) =>
          row.id === org.id
            ? { ...row, product_federation_enabled: next, productFederationEnabled: next }
            : row
        ),
      }));
    } catch (e) {
      setOrgActionError(e.message || 'Could not update Federation entitlement');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <ShieldCheck className="w-5 h-5" />
            <span className="font-semibold">Tactmark Admin</span>
            <span className="text-white/40 text-xs ml-2">Internal only</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/70 text-sm">{adminEmail}</span>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tab nav */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab===tab.id ? 'border-navy-900 text-navy-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div>
            <h1 className="text-2xl font-bold text-navy-900 mb-6">Platform Overview</h1>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              <StatCard icon={Building2} label="Organizations" value={overview.totalOrganizations} />
              <StatCard icon={TrendingUp} label="Active" value={overview.activeOrganizations} />
              <StatCard icon={Users} label="Users" value={overview.totalUsers} />
              <StatCard icon={FileText} label="Matches" value={overview.totalMatches} />
              <StatCard icon={TrendingUp} label="Reports" value={overview.totalReports} />
              <StatCard icon={Film} label="Footage uploads" value={overview.totalFootageUploads} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <div className="bg-navy-50 border border-navy-100 rounded-xl p-4">
                <p className="text-2xl font-bold text-navy-900">{overview.trialOrganizations}</p>
                <p className="text-sm text-gray-500">On trial</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <p className="text-2xl font-bold text-navy-900">{overview.trialEndingSoon}</p>
                <p className="text-sm text-gray-500">Trial ending &lt;3 days</p>
              </div>
              <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                <p className="text-2xl font-bold text-navy-900">{overview.trialExpired}</p>
                <p className="text-sm text-gray-500">Trial expired</p>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                <p className="text-2xl font-bold text-navy-900">{overview.paidSubscribers}</p>
                <p className="text-sm text-gray-500">Paid subscribers</p>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-sm text-gray-600">
              <p><strong>Org breakdown:</strong> {overview.totalClubs} Clubs / {overview.totalAcademies} Academies / {overview.totalFederations} Federations / {overview.totalSoloCoaches} Solo coaches</p>
            </div>
          </div>
        )}

        {/* ── ORGANIZATIONS ── */}
        {activeTab === 'organizations' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-navy-900">Organizations ({organizations.length})</h2>
              <div className="relative"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" /><input value={orgSearch} onChange={e=>setOrgSearch(e.target.value)} placeholder="Search..." className="pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-48" /></div>
            </div>
            {orgActionError && (
              <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{orgActionError}</div>
            )}
            <div className="border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                  <th className="text-left px-4 py-3">Organization</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Country</th>
                  <th className="text-left px-4 py-3">Plan</th><th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Post-Match</th><th className="text-left px-4 py-3">Federation</th>
                  <th className="text-right px-4 py-3">Teams</th>
                  <th className="text-right px-4 py-3">Users</th><th className="text-right px-4 py-3">Reports</th><th className="text-right px-4 py-3">Joined</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredOrgs.map(o => (
                    <tr key={o.id}>
                      <td className="px-4 py-3 text-navy-900 font-medium">{o.name}</td>
                      <td className="px-4 py-3 text-gray-600">{o.organization_type}</td>
                      <td className="px-4 py-3 text-gray-600">{o.country || 'Unknown'}</td>
                      <td className="px-4 py-3 text-gray-600 capitalize">{o.plan}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-1 rounded-full ${o.status==='Active'?'bg-green-50 text-green-700':o.status==='Trial'?'bg-navy-50 text-navy-700':'bg-gray-100 text-gray-500'}`}>{o.status}</span></td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{postMatchEnabled(o) ? 'Enabled' : 'Disabled'}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggleFederation(o)}
                          className={`text-xs font-medium px-2.5 py-1 rounded border transition-colors ${
                            federationEnabled(o)
                              ? 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {federationEnabled(o) ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">{o.teams}</td>
                      <td className="px-4 py-3 text-right">{o.users}</td>
                      <td className="px-4 py-3 text-right">{o.reports}</td>
                      <td className="px-4 py-3 text-right text-gray-400">{o.created_at?.slice(0,10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredOrgs.length === 0 && <p className="text-center py-8 text-gray-400">No organizations found.</p>}
            </div>
          </div>
        )}

        {/* ── TRIALS & BILLING ── */}
        {activeTab === 'trials' && (
          <div>
            <h2 className="text-xl font-bold text-navy-900 mb-6">Trials and Billing</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Activity} label="Active trials" value={trials.activeTrials} />
              <StatCard icon={Activity} label="Expiring (3d)" value={trials.expiringTrials} />
              <StatCard icon={Activity} label="Expired" value={trials.expiredTrials} />
              <StatCard icon={CreditCard} label="Converted" value={trials.convertedOrgs} />
            </div>
            {trials.webhookEvents?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-navy-900 font-medium mb-3">Webhook events (processed)</h3>
                <div className="flex gap-3 flex-wrap">
                  {trials.webhookEvents.map(e => (
                    <div key={e.event_type} className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm">
                      <span className="font-medium text-navy-900">{e.event_type}</span>
                      <span className="text-gray-400 ml-2">&times;{e.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                  <th className="text-left px-4 py-3">Organization</th><th className="text-left px-4 py-3">Country</th>
                  <th className="text-left px-4 py-3">Plan</th><th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Trial ends</th><th className="text-left px-4 py-3">Joined</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(trials.recentOrgs||[]).map((o,i) => (
                    <tr key={i}>
                      <td className="px-4 py-3 text-navy-900 font-medium">{o.name}</td>
                      <td className="px-4 py-3 text-gray-600">{o.country || 'Unknown'}</td>
                      <td className="px-4 py-3 text-gray-600 capitalize">{o.plan}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-1 rounded-full ${o.status==='Active'?'bg-green-50 text-green-700':o.status==='Trial'?'bg-navy-50 text-navy-700':'bg-gray-100 text-gray-500'}`}>{o.status}</span></td>
                      <td className="px-4 py-3 text-gray-500">{o.trial_ends_at?.slice(0,10) || 'N/a'}</td>
                      <td className="px-4 py-3 text-gray-400">{o.created_at?.slice(0,10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── USAGE ── */}
        {activeTab === 'usage' && (
          <div>
            <h2 className="text-xl font-bold text-navy-900 mb-6">Usage</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Users} label="DAU" value={usage.dau} sub="Distinct users today" />
              <StatCard icon={Users} label="WAU" value={usage.wau} sub="Last 7 days" />
              <StatCard icon={Users} label="MAU" value={usage.mau} sub="Last 30 days" />
              <StatCard icon={Activity} label="Sessions (7d)" value={usage.sessionsThisWeek} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <StatCard icon={FileText} label="Matches this week" value={usage.matchesThisWeek} />
              <StatCard icon={TrendingUp} label="Reports this week" value={usage.reportsThisWeek} />
              <StatCard icon={Activity} label="Analyses today" value={usage.analysesToday} />
            </div>
          </div>
        )}

        {/* ── COUNTRIES ── */}
        {activeTab === 'countries' && (
          <div>
            <h2 className="text-xl font-bold text-navy-900 mb-4">Country Analytics</h2>
            <div className="border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                  <th className="text-left px-4 py-3">Country</th><th className="text-right px-4 py-3">Orgs</th><th className="text-right px-4 py-3">Active</th>
                  <th className="text-right px-4 py-3">Trial</th><th className="text-right px-4 py-3">Teams</th><th className="text-right px-4 py-3">Matches</th><th className="text-right px-4 py-3">Reports</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {countries.map(c => (
                    <tr key={c.country}>
                      <td className="px-4 py-3 text-navy-900 font-medium">{c.country || 'Unknown'}</td>
                      <td className="px-4 py-3 text-right">{c.total_organizations}</td>
                      <td className="px-4 py-3 text-right">{c.active_accounts}</td>
                      <td className="px-4 py-3 text-right">{c.trial_accounts}</td>
                      <td className="px-4 py-3 text-right">{c.teams}</td>
                      <td className="px-4 py-3 text-right">{c.matches}</td>
                      <td className="px-4 py-3 text-right">{c.reports}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {countries.length === 0 && <p className="text-center py-8 text-gray-400">No country data yet.</p>}
            </div>
          </div>
        )}

        {/* ── CONTACTS ── */}
        {activeTab === 'contacts' && (
          <div>
            <h2 className="text-xl font-bold text-navy-900 mb-4">Contact Submissions ({contacts.length})</h2>
            <div className="border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Organization</th>
                  <th className="text-left px-4 py-3">Lead</th>
                  <th className="text-left px-4 py-3">Process / problem</th>
                  <th className="text-left px-4 py-3">Delivery</th>
                  <th className="text-right px-4 py-3">Date</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {contacts.map((c,i) => (
                    <tr key={c.id || i}>
                      <td className="px-4 py-3 text-navy-900 font-medium">{c.name}</td>
                      <td className="px-4 py-3 text-gray-600">{c.email}</td>
                      <td className="px-4 py-3 text-gray-600">
                        <div>{c.organization || '--'}</div>
                        <div className="text-xs text-gray-400">{c.organization_type || c.country || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        <div>{c.lead_source || '--'}</div>
                        <div>{c.desired_timing || ''}</div>
                        <div>{[c.num_teams && `${c.num_teams} teams`, c.num_staff && `${c.num_staff} users`].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-xs">
                        <div className="truncate" title={c.current_development_reporting_process || c.current_analysis_process || ''}>
                          {c.current_development_reporting_process || c.current_analysis_process || '--'}
                        </div>
                        <div className="truncate text-xs text-gray-400" title={c.main_operational_problem || c.message || ''}>
                          {c.main_operational_problem || c.message || ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {c.delivery_status || c.submission_status || '--'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-400">{c.created_at?.slice(0,10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {contacts.length === 0 && <p className="text-center py-8 text-gray-400">No contact submissions yet.</p>}
            </div>
          </div>
        )}

        {/* ── CONTENT ── */}
        {activeTab === 'content' && <ContentTab data={content} />}

        {/* ── SECURITY ── */}
        {activeTab === 'security' && (
          <div>
            <h2 className="text-xl font-bold text-navy-900 mb-6">Security and Audit</h2>
            {(security.lockedIPs||[]).length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <h3 className="text-red-700 font-semibold mb-2">Rate-limited IPs (5+ failed attempts in 1h)</h3>
                {security.lockedIPs.map((ip,i) => (
                  <p key={i} className="text-sm text-red-600">{ip.ip} {ip.attempts} attempts (last: {ip.last_attempt?.slice(0,19)})</p>
                ))}
              </div>
            )}
            <div className="mb-6">
              <h3 className="text-navy-900 font-medium mb-3">Platform Admins</h3>
              <div className="flex gap-3 flex-wrap">
                {(security.platformAdmins||[]).map(a => (
                  <div key={a.id} className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm text-navy-900">{a.email}</div>
                ))}
              </div>
            </div>
            <h3 className="text-navy-900 font-medium mb-3">Recent Auth Attempts</h3>
            <div className="border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                  <th className="text-left px-4 py-3">IP</th><th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Result</th><th className="text-right px-4 py-3">Time</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(security.recentAttempts||[]).slice(0,50).map((a,i) => (
                    <tr key={i}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{a.ip}</td>
                      <td className="px-4 py-3 text-gray-600">{a.email}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-1 rounded-full ${a.success?'bg-green-50 text-green-700':'bg-red-50 text-red-600'}`}>{a.success?'Success':'Failed'}</span></td>
                      <td className="px-4 py-3 text-right text-gray-400 text-xs">{a.attempted_at?.slice(0,19)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(security.recentAttempts||[]).length === 0 && <p className="text-center py-8 text-gray-400">No auth events recorded.</p>}
            </div>
          </div>
        )}

        {/* ── SYSTEM HEALTH ── */}
        {activeTab === 'system_health' && (
          <div>
            <h2 className="text-xl font-bold text-navy-900 mb-6">System Health</h2>
            <div className="grid gap-3">
              {systemHealth.map((c,i) => (
                <div key={i} className={`flex items-center justify-between p-4 rounded-xl border ${c.status==='ok'?'bg-green-50 border-green-100':'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${c.status==='ok'?'bg-green-500':'bg-red-500'}`} />
                    <span className="font-medium text-sm text-navy-900">{c.name}</span>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    {c.rowCount !== undefined && <span>{c.rowCount.toLocaleString()} rows</span>}
                    {c.message && <span className="text-red-600">{c.message}</span>}
                    <span className={`ml-3 text-xs font-medium ${c.status==='ok'?'text-green-600':'text-red-600'}`}>{c.status.toUpperCase()}</span>
                  </div>
                </div>
              ))}
              {systemHealth.length === 0 && <p className="text-gray-400">No health data.</p>}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
